import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Sparkles, X, ArrowRight } from "lucide-react";

const STORAGE_KEY = "at_demo_popup_dismissed_v1";
const COOLDOWN_HOURS = 24;

export default function SmartDemoPopup() {
    const { pathname } = useLocation();
    const [open, setOpen] = useState(false);
    const [scrolled70, setScrolled70] = useState(false);

    useEffect(() => {
        // Hide on admin / register / pay-now / contact (already converting)
        if (pathname.startsWith("/admin") || pathname.startsWith("/pay/") || pathname === "/register" || pathname === "/pay-now" || pathname === "/contact") {
            return;
        }

        // Check cooldown
        const dismissedAt = parseInt(localStorage.getItem(STORAGE_KEY) || "0", 10);
        if (dismissedAt && Date.now() - dismissedAt < COOLDOWN_HOURS * 3600 * 1000) {
            return;
        }

        let timeFired = false;
        let exitFired = false;

        const fire = (reason) => {
            if (timeFired || exitFired) return;
            if (reason === "time") timeFired = true;
            if (reason === "exit") exitFired = true;
            setOpen(true);
        };

        // Trigger 1: scroll past 60%
        const onScroll = () => {
            const h = document.documentElement;
            const scrollPct = (h.scrollTop + window.innerHeight) / h.scrollHeight;
            if (scrollPct >= 0.6 && !scrolled70) {
                setScrolled70(true);
                fire("scroll");
            }
        };

        // Trigger 2: time-based — 25 seconds
        const t = setTimeout(() => fire("time"), 25000);

        // Trigger 3: exit intent (desktop only) — mouse leaves toward top
        const onMouseLeave = (e) => {
            if (e.clientY <= 0 && window.innerWidth > 1024) fire("exit");
        };

        window.addEventListener("scroll", onScroll, { passive: true });
        document.addEventListener("mouseleave", onMouseLeave);

        return () => {
            clearTimeout(t);
            window.removeEventListener("scroll", onScroll);
            document.removeEventListener("mouseleave", onMouseLeave);
        };
    }, [pathname, scrolled70]);

    const close = () => {
        setOpen(false);
        localStorage.setItem(STORAGE_KEY, String(Date.now()));
    };

    if (!open) return null;

    return (
        <div
            className="fixed inset-0 z-[70] flex items-end sm:items-center justify-center bg-black/50 backdrop-blur-sm p-0 sm:p-6 animate-fade-up"
            onClick={close}
            data-testid="demo-popup"
        >
            <div
                className="bg-white w-full sm:max-w-lg sm:rounded-3xl rounded-t-3xl shadow-2xl overflow-hidden border border-slate-200 relative"
                onClick={(e) => e.stopPropagation()}
            >
                <button
                    onClick={close}
                    aria-label="Close"
                    data-testid="demo-popup-close"
                    className="absolute top-3 right-3 w-9 h-9 rounded-full bg-white/80 hover:bg-white border border-slate-200 flex items-center justify-center text-slate-600 hover:text-slate-900 z-10"
                >
                    <X className="w-4 h-4" />
                </button>

                <div className="bg-gradient-to-br from-[#1E3A8A] via-[#1E40AF] to-[#1E3A8A] px-6 pt-7 pb-6 text-white">
                    <div className="inline-flex items-center gap-2 bg-amber-500 text-[#0F172A] text-[10px] font-bold uppercase tracking-[0.2em] px-3 py-1 rounded-full mb-4">
                        <Sparkles className="w-3 h-3" /> Free 1:1 Demo
                    </div>
                    <h3 className="font-heading text-2xl sm:text-3xl font-bold leading-tight mb-2">
                        Don't pick a tutor in the dark.
                    </h3>
                    <p className="text-blue-100 text-sm leading-relaxed">
                        Try one full 1:1 class with a verified Anand Tutor — completely free, no card, no commitment.
                    </p>
                </div>

                <div className="px-6 py-5 space-y-3">
                    <div className="grid grid-cols-3 gap-3 text-center">
                        {[
                            ["12+", "yrs trusted"],
                            ["50k+", "students"],
                            ["98%", "pass rate"],
                        ].map(([n, l]) => (
                            <div key={l} className="bg-slate-50 rounded-xl py-2.5 px-2 border border-slate-100">
                                <div className="font-heading font-bold text-[#1E3A8A] text-base">{n}</div>
                                <div className="text-[10px] uppercase tracking-[0.15em] text-slate-500 mt-0.5">{l}</div>
                            </div>
                        ))}
                    </div>

                    <Link
                        to="/register"
                        onClick={close}
                        data-testid="demo-popup-cta"
                        className="w-full inline-flex items-center justify-center gap-2 bg-amber-500 text-[#0F172A] font-bold py-3.5 rounded-xl hover:bg-amber-400 active:bg-amber-600 transition-colors"
                    >
                        Claim My Free Demo Class <ArrowRight className="w-4 h-4" />
                    </Link>

                    <button
                        onClick={close}
                        className="w-full text-xs text-slate-400 hover:text-slate-600 py-1"
                    >
                        Maybe later
                    </button>
                </div>
            </div>
        </div>
    );
}
